import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const SAUDE = {
  boa: { label: "Saúde boa", cls: "bg-emerald-100 text-emerald-700" },
  atencao: { label: "Atenção", cls: "bg-amber-100 text-amber-700" },
  critica: { label: "Crítica", cls: "bg-red-100 text-red-700" },
};
const SEV = { alta: "bg-red-100 text-red-700", media: "bg-amber-100 text-amber-700", baixa: "bg-slate-100 text-slate-600" };

export function ResumoIAMensal() {
  const [anoMes, setAnoMes] = useState(() => new Date().toISOString().slice(0, 7));
  const [resumo, setResumo] = useState(null);
  const [gerando, setGerando] = useState(false);

  const carregar = useCallback(async (am) => {
    try {
      const r = await axios.get(`${API}/financeiro/resumo-ia-mensal`, { params: { ano_mes: am } });
      setResumo(r.data?.existe === false ? null : r.data);
    } catch { setResumo(null); }
  }, []);
  useEffect(() => { carregar(anoMes); }, [anoMes, carregar]);

  const gerar = async () => {
    setGerando(true);
    try {
      const r = await axios.post(`${API}/financeiro/resumo-ia-mensal`, { ano_mes: anoMes });
      setResumo(r.data);
      toast.success("Parecer do mês gerado pela IA");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao gerar parecer")); }
    finally { setGerando(false); }
  };

  const saude = resumo && (SAUDE[resumo.saude] || SAUDE.atencao);

  return (
    <Card className="border-0 shadow-sm border-l-4 border-l-purple-400" data-testid="resumo-ia-mensal">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading font-bold text-luvcorp-navy flex items-center gap-2 text-base">
            <Sparkles className="h-5 w-5 text-purple-600" />Parecer IA do Mês
          </h2>
          {saude && <Badge className={`text-[10px] ${saude.cls}`} data-testid="badge-saude-caixa">{saude.label}</Badge>}
          <div className="flex-1" />
          <Input type="month" value={anoMes} onChange={(e) => setAnoMes(e.target.value)}
            className="w-40 h-9" data-testid="input-mes-resumo-ia" />
          <Button size="sm" onClick={gerar} disabled={gerando}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="btn-gerar-resumo-ia">
            {gerando ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
            {gerando ? "Analisando…" : resumo ? "Atualizar parecer" : "Gerar parecer"}
          </Button>
        </div>

        {gerando && !resumo && (
          <p className="text-sm text-slate-500 py-3 text-center">A IA está analisando o caixa do mês contra a média dos 3 meses anteriores… até 1 minuto.</p>
        )}
        {!gerando && !resumo && (
          <p className="text-sm text-slate-400 py-2">Nenhum parecer gerado para este mês. Clique em <b>Gerar parecer</b> para a IA analisar receitas, despesas por categoria e gastos fora do padrão.</p>
        )}
        {resumo && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-4 text-xs text-slate-600">
              <span>Receitas <b className="font-mono text-emerald-700">{fmtBRL(resumo.totais?.receitas)}</b></span>
              <span>Despesas <b className="font-mono text-red-700">{fmtBRL(resumo.totais?.despesas)}</b></span>
              <span>Saldo <b className={`font-mono ${(resumo.totais?.saldo || 0) >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmtBRL(resumo.totais?.saldo)}</b></span>
              <span className="text-slate-400">gerado em {new Date(resumo.gerada_em).toLocaleString("pt-BR")}</span>
            </div>
            <p className="text-sm p-3 rounded-lg bg-purple-50 border border-purple-100 text-slate-700" data-testid="resumo-ia-texto">{resumo.resumo}</p>
            {(resumo.alertas || []).length > 0 && (
              <div className="space-y-1.5">
                {(resumo.alertas || []).map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg border bg-white" data-testid={`resumo-ia-alerta-${i}`}>
                    <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${a.severidade === "alta" ? "text-red-500" : a.severidade === "media" ? "text-amber-500" : "text-slate-400"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-luvcorp-navy flex items-center gap-2 flex-wrap">
                        {a.titulo}<Badge className={`text-[10px] ${SEV[a.severidade] || SEV.baixa}`}>{a.severidade}</Badge>
                      </p>
                      <p className="text-xs text-slate-600 leading-relaxed">{a.descricao}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(resumo.destaques || []).length > 0 && (
              <div className="space-y-1">
                {(resumo.destaques || []).map((d, i) => (
                  <p key={i} className="text-xs text-slate-600 flex items-start gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />{d}
                  </p>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ResumoIAMensal;
